// Drill de LÍNEA del directivo — hora a hora, SOLO LECTURA (brief §4.2).
//
// Reutiliza el endpoint C6b del coordinador (/coordinador/linea/:id/tiempo),
// abierto al rol directivo desde 2026-06-11. Responde "¿por qué pierde esta
// línea?": KPIs del período, gap paro vs ritmo, serie temporal (por hora en
// Hoy, por día en rangos), pareto y timeline de paros, y desglose por SKU.
// Cero CTAs de edición/corrección — eso es mundo coordinador.

import { useNavigate, useParams } from 'react-router-dom';
import type { CoordinadorLineaTiempo } from 'schemas';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components/ui/card';
import { KpiCard } from '../../components/ui/kpi-card';
import { Spinner } from '../../components/ui/spinner';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useCoordinadorLineaTiempo } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import {
  BarraPeriodo,
  FMT_DIA_CORTO,
  enlaceConPeriodo,
  limpiar,
  parseUTC,
  usePeriodo,
} from './lib/periodo';
import { horasMin, miles } from './lib/formato';

const SEMAFORO: Record<string, string> = {
  verde: 'bg-ok',
  amarillo: 'bg-warn',
  rojo: 'bg-crit',
  sin_data: 'bg-fg-tertiary',
};

export function LineaDetallePage() {
  const { lineaId } = useParams<{ lineaId: string }>();
  const navigate = useNavigate();
  const ctrl = usePeriodo(undefined, 'hoy', { url: true });
  const { periodo, preset } = ctrl;

  // C6b discrimina el eje por granularidad: hora|dia → serie por bloque-hora;
  // semana|mes → serie por día.
  const dias =
    (parseUTC(periodo.hasta).getTime() - parseUTC(periodo.desde).getTime()) / 86_400_000 + 1;
  const granularidad =
    preset === 'hoy' || dias <= 1
      ? ('dia' as const)
      : dias <= 7
        ? ('semana' as const)
        : ('mes' as const);

  const detalle = useCoordinadorLineaTiempo(lineaId ?? null, {
    desde: periodo.desde,
    hasta: periodo.hasta,
    granularidad,
  });

  return (
    <DirectivoLayout>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <BarraPeriodo ctrl={ctrl} />
        <div className="flex-1" />
      </div>
      <p className="mb-4 text-[11px] text-fg-tertiary">
        <button
          type="button"
          onClick={() => navigate(enlaceConPeriodo('/directivo/lineas', ctrl))}
          className="underline hover:text-fg"
        >
          Líneas de producción
        </button>
        {' / '}
        {detalle.data ? `${detalle.data.linea.planta_nombre} / ${detalle.data.linea.codigo}` : '…'}
      </p>

      {detalle.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : detalle.isError || !detalle.data ? (
        <Card className="p-6 text-sm text-fg-secondary">
          No se pudo cargar el detalle de la línea. Intente de nuevo en unos segundos.
        </Card>
      ) : (
        <DetalleContenido data={detalle.data} />
      )}
    </DirectivoLayout>
  );
}

function DetalleContenido({ data }: { data: CoordinadorLineaTiempo }) {
  const k = data.kpis;
  const cumpl = k.cumpl_meta_pct !== null ? Math.round(k.cumpl_meta_pct) : null;
  const gapTotal = Math.max(0, k.gap_total);

  const serie =
    data.eje === 'hora'
      ? data.serie_hora.map((p) => ({
          label: p.hora.slice(0, 5),
          producido: p.producido,
          meta: Math.round(p.meta_h),
          cumpl: p.cumpl_meta_pct !== null ? Math.round(p.cumpl_meta_pct) : null,
          paro_min: p.paro_min,
        }))
      : data.serie_dia.map((p) => ({
          label: limpiar(FMT_DIA_CORTO.format(parseUTC(p.periodo))),
          producido: p.producido,
          meta: Math.round(p.meta),
          cumpl: p.cumpl_meta_pct !== null ? Math.round(p.cumpl_meta_pct) : null,
          paro_min: p.paro_min,
        }));

  return (
    <>
      {/* ── Header de línea + KPIs ── */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'inline-block size-2.5 rounded-full',
            SEMAFORO[data.estado_semaforo] ?? 'bg-fg-tertiary',
          )}
        />
        <h1 className="text-lg font-semibold text-fg">{data.linea.codigo}</h1>
        <span className="text-sm text-fg-tertiary">
          {data.linea.nombre} · {data.linea.area_nombre} · {data.linea.planta_nombre}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="py-4">
          <KpiCard
            label="Cumplimiento capacidad"
            value={cumpl !== null ? `${cumpl}` : 'n/s'}
            valueSuffix={cumpl !== null ? '%' : undefined}
            sub={`${miles(k.producido)} de ${miles(k.meta)} paq`}
          />
        </Card>
        <Card className="py-4">
          <KpiCard
            label="Paros"
            value={horasMin(k.paro_min)}
            sub={`${k.paros_count} eventos que afectaron disponibilidad`}
          />
        </Card>
        <Card className="py-4">
          <KpiCard
            label="Horas efectivas"
            value={`${k.horas_efectivas.toLocaleString('es-CO', { maximumFractionDigits: 1 })}h`}
            sub={
              k.golpes_aplica && k.golpes_real !== null && k.golpes_estandar !== null
                ? `ritmo ${k.golpes_real.toLocaleString('es-CO', { maximumFractionDigits: 1 })} vs estándar ${k.golpes_estandar.toLocaleString('es-CO', { maximumFractionDigits: 1 })} golpes/min`
                : 'bloques capturados menos tiempo de paro'
            }
          />
        </Card>
        <Card className="py-4">
          <KpiCard
            label="¿Por qué pierde?"
            value={
              gapTotal > 0 ? (
                <span className="tabular-nums">
                  {Math.round(data.diagnostico.pct_por_paro)}%
                  <span className="ml-1.5 mr-3 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                    paro
                  </span>
                  {Math.round(data.diagnostico.pct_por_ritmo)}%
                  <span className="ml-1.5 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                    ritmo
                  </span>
                </span>
              ) : (
                'Sin gap'
              )
            }
            sub={
              data.diagnostico.causal_top
                ? `Causal top: ${data.diagnostico.causal_top.nombre} (${horasMin(data.diagnostico.causal_top.minutos)})`
                : `Gap total: ${miles(gapTotal)} paq`
            }
          />
        </Card>
      </div>

      {/* ── Serie temporal ── */}
      <Card className="mt-5 p-5">
        <h2 className="mb-4 text-sm font-medium text-fg">
          {data.eje === 'hora' ? 'Hora a hora' : 'Día a día'} — producción vs meta de capacidad
        </h2>
        {serie.length === 0 ? (
          <div className="flex h-56 items-center justify-center text-sm text-fg-tertiary">
            Sin capturas en el período
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={serie} margin={{ top: 12, right: 0, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: 'var(--color-fg-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                yAxisId="unds"
                tickFormatter={(v) => miles(Number(v))}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <YAxis
                yAxisId="pct"
                orientation="right"
                domain={[0, 130]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)' }}
                contentStyle={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v, name) => {
                  const key = String(name);
                  if (key === 'Cumplimiento') return [`${Math.round(Number(v))}%`, key];
                  if (key === 'Paro') return [horasMin(Number(v)), key];
                  return [`${miles(Number(v))} paq`, key];
                }}
              />
              <Bar
                yAxisId="unds"
                dataKey="meta"
                name="Meta capacidad"
                fill="var(--color-border-strong)"
                radius={[2, 2, 0, 0]}
                maxBarSize={30}
              />
              <Bar
                yAxisId="unds"
                dataKey="producido"
                name="Producido"
                fill="var(--color-fg-tertiary)"
                radius={[2, 2, 0, 0]}
                maxBarSize={30}
              />
              <Line
                yAxisId="pct"
                dataKey="cumpl"
                name="Cumplimiento"
                stroke="var(--color-info)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={{ r: 2.5, fill: 'var(--color-info)', strokeWidth: 0 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* ── Paros + SKUs ── */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-medium text-fg">Paros del período</h2>
          {data.paros.pareto.length === 0 ? (
            <p className="text-sm text-fg-tertiary">Sin paros que afecten disponibilidad</p>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {data.paros.pareto.map((p) => (
                  <div key={p.causal_madre_nombre} className="flex items-center gap-3">
                    <span
                      className="inline-block size-2 shrink-0 rounded-full"
                      style={{ background: p.categoria_color ?? 'var(--color-fg-tertiary)' }}
                    />
                    <span className="w-52 truncate text-[12px] text-fg-secondary">
                      {p.causal_madre_nombre}
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-overlay">
                      <div
                        className="h-full rounded-sm"
                        style={{
                          width: `${Math.max(4, (p.minutos / Math.max(1, data.paros.pareto[0]?.minutos ?? 1)) * 100)}%`,
                          background: p.categoria_color ?? 'var(--color-fg-tertiary)',
                        }}
                      />
                    </div>
                    <span className="whitespace-nowrap font-mono text-[11px] tabular-nums text-fg-secondary">
                      {horasMin(p.minutos)} · {p.paros_count} ev
                    </span>
                  </div>
                ))}
              </div>
              {data.paros.timeline.length > 0 ? (
                <>
                  <p className="mb-1.5 mt-4 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                    Timeline
                  </p>
                  <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                    {data.paros.timeline.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11.5px]">
                        <span
                          className="inline-block size-1.5 shrink-0 rounded-full"
                          style={{ background: t.categoria_color ?? 'var(--color-fg-tertiary)' }}
                        />
                        <span className="w-24 font-mono tabular-nums text-fg-tertiary">
                          {t.hora_inicio.slice(5, 16).replace('T', ' ')}
                        </span>
                        <span className="flex-1 truncate text-fg-secondary">
                          {t.causal_madre_nombre} — {t.causal_nombre}
                        </span>
                        <span className="font-mono tabular-nums text-fg-secondary">
                          {horasMin(t.minutos)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-sm font-medium text-fg">Por SKU</h2>
          {data.por_sku.filter((s) => s.corrio).length === 0 ? (
            <p className="text-sm text-fg-tertiary">Sin SKUs corridos en el período</p>
          ) : (
            <div className="flex flex-col gap-2">
              {data.por_sku
                .filter((s) => s.corrio)
                .map((s) => (
                  <div key={s.sku_id} className="flex items-center gap-3 text-[12px]">
                    <span className="w-24 truncate font-mono uppercase text-fg">
                      {s.sku_codigo}
                    </span>
                    <span className="flex-1 truncate text-fg-tertiary">{s.sku_descripcion}</span>
                    <span className="whitespace-nowrap font-mono tabular-nums text-fg-secondary">
                      {miles(s.producido)} paq ·{' '}
                      {s.cumpl_meta_pct !== null ? `${Math.round(s.cumpl_meta_pct)}%` : 'n/s'} ·{' '}
                      {s.horas_corridas.toLocaleString('es-CO', { maximumFractionDigits: 1 })}h
                    </span>
                  </div>
                ))}
            </div>
          )}
          {data.comparativo ? (
            <p className="mt-4 border-t border-border pt-2.5 text-[11px] text-fg-tertiary">
              Período anterior ({data.comparativo.desde} – {data.comparativo.hasta}):{' '}
              {miles(data.comparativo.kpis.producido)} paq ·{' '}
              {data.comparativo.kpis.cumpl_meta_pct !== null
                ? `${Math.round(data.comparativo.kpis.cumpl_meta_pct)}%`
                : 'n/s'}{' '}
              · {horasMin(data.comparativo.kpis.paro_min)} de paro
            </p>
          ) : null}
        </Card>
      </div>
    </>
  );
}
